import { pacificToday } from '../../../shared/dates';
import type { Feature } from '../../../shared/types';
import { shell } from '../app';
import { apiFetch, NetworkError } from '../data/api';
import { createFeature, deleteFeature, getWatchlistData, onStoreChange, restoreFeature } from '../data/store';
import { runSync, syncAndSettle } from '../data/sync';
import { formatDate, timeAgo, todayLocal } from '../logic/dates';
import { buildWatchlist } from '../logic/watchlist';
import type { WatchlistEntry } from '../logic/watchlist';
import { btn, confirmDialog, empty, input, section, stageTag } from '../ui/components';
import { h, replaceChildren, span } from '../ui/dom';
import { navigate } from '../ui/router';
import type { View } from '../ui/router';

/** Watchlist (M4): features owners asked for → who asked → "Mark as launched" fires the
 *  good-news drafts. Reading + capture are offline (mirror); launching needs the server. */

/** Queued writes go first: the feature — or the asks that decide who gets a draft — may exist only
 *  on the phone yet. */
const launchFeature = async (id: string): Promise<{ feature: Feature; drafts_created: number }> => {
  await syncAndSettle();
  return apiFetch<{ feature: Feature; drafts_created: number }>(
    'POST',
    `/api/features/${encodeURIComponent(id)}/launch`,
    {},
  );
};

export const watchlistView: View = ({ root }) => {
  shell.setChrome(true);
  shell.setTitle('Watchlist', { back: '#/home' });
  const host = h('div', { class: 'page page--watchlist' });
  root.appendChild(host);
  let openId: string | null = null;
  const today = todayLocal();

  const entryCard = (entry: WatchlistEntry): HTMLElement => {
    const { feature, asks, leadCount, draftCount } = entry;
    const open = openId === feature.id;
    const launched = feature.status === 'launched';
    return h(
      'article',
      {
        class: ['feature', launched ? 'feature--launched' : '', open ? 'feature--open' : '']
          .filter(Boolean)
          .join(' '),
      },
      h(
        'button',
        {
          type: 'button',
          class: 'feature__head',
          onClick: () => {
            openId = open ? null : feature.id;
            void render();
          },
        },
        h(
          'div',
          { class: 'feature__headtext' },
          h('div', { class: 'feature__name' }, feature.name),
          h(
            'div',
            { class: 'feature__meta' },
            launched
              ? span(
                  'posbadge draft__status--copied',
                  `launched ${feature.launched_at ? formatDate(pacificToday(new Date(feature.launched_at)), today) : ''}`,
                )
              : span('posbadge', 'missing'),
            span('mono muted', ` ${leadCount} lead${leadCount === 1 ? '' : 's'}`),
          ),
        ),
        span('draft__chev', open ? '▾' : '▸'),
      ),
      open
        ? h(
            'div',
            { class: 'feature__body' },
            // Action FIRST and full-width — with a long ask list it was below the fold and easy to miss.
            launched
              ? h(
                  'p',
                  { class: 'feature__launchednote muted small' },
                  'Launched — good-news drafts live in Emails.',
                )
              : btn(
                  `Mark as launched${draftCount ? ` → drafts ${draftCount} email${draftCount === 1 ? '' : 's'}` : ''}`,
                  {
                    variant: 'primary',
                    class: 'w-full',
                    onClick: async (ev) => {
                      const b = ev.currentTarget as HTMLButtonElement;
                      if (
                        !confirmDialog(
                          `Mark "${feature.name}" as launched?${draftCount ? ` This drafts a good-news email for ${draftCount} lead${draftCount === 1 ? '' : 's'}.` : ''}`,
                        )
                      )
                        return;
                      b.disabled = true;
                      try {
                        const res = await launchFeature(feature.id);
                        shell.toast(
                          res.drafts_created > 0
                            ? `Launched — ${res.drafts_created} draft${res.drafts_created === 1 ? '' : 's'} ready, tap to open`
                            : 'Marked as launched',
                          {
                            kind: 'success',
                            duration: 6000,
                            onClick: res.drafts_created > 0 ? () => navigate('#/emails') : undefined,
                          },
                        );
                        void runSync(); // pull the launched status into the mirror
                      } catch (err) {
                        shell.toast(
                          err instanceof NetworkError
                            ? 'Launching needs a connection.'
                            : err instanceof Error
                              ? err.message
                              : String(err),
                          { kind: 'error' },
                        );
                        b.disabled = false;
                      }
                    },
                  },
                ),
            asks.length
              ? asks.map((a) =>
                  h(
                    'button',
                    {
                      type: 'button',
                      class: 'feature__ask',
                      onClick: () => navigate(`#/leads/${encodeURIComponent(a.lead.id)}`),
                    },
                    h(
                      'div',
                      { class: 'feature__asktext' },
                      h(
                        'div',
                        { class: 'feature__asklead' },
                        a.lead.business_name,
                        ' ',
                        stageTag(a.lead.stage),
                      ),
                      h(
                        'div',
                        { class: 'feature__askmeta' },
                        a.visit ? `asked ${timeAgo(a.visit.visited_at)}` : 'asked (no visit attached)',
                        a.request.notes ? ` · “${a.request.notes}”` : '',
                      ),
                    ),
                  ),
                )
              : empty('No one has asked for this yet.'),
            h(
              'div',
              { class: 'feature__actions' },
              btn('Remove', {
                variant: 'ghost',
                size: 'sm',
                onClick: async () => {
                  let archive;
                  try {
                    archive = await deleteFeature(feature.id);
                  } catch (err) {
                    shell.toast(`Could not delete: ${err instanceof Error ? err.message : String(err)}`, {
                      kind: 'error',
                    });
                    return;
                  }
                  if (!archive) return;
                  shell.toast(`Stopped tracking “${feature.name}” — tap to undo`, {
                    duration: 8000,
                    onClick: () =>
                      void restoreFeature(archive).catch((err: unknown) =>
                        shell.toast(`Could not undo: ${err instanceof Error ? err.message : String(err)}`, {
                          kind: 'error',
                        }),
                      ),
                  });
                },
              }),
            ),
          )
        : null,
    );
  };

  const newInput = input({
    placeholder: 'Track a feature owners ask about…',
    maxlength: 80,
    enterkeyhint: 'done',
    required: true,
  });
  newInput.setAttribute('aria-describedby', 'feature-error');
  const validation = h('p', { id: 'feature-error', role: 'alert' });
  newInput.addEventListener('input', () => { validation.textContent = ''; newInput.removeAttribute('aria-invalid'); });
  const add = async () => {
    const name = newInput.value.trim();
    if (!name) {
      validation.textContent = 'Enter a feature to track.';
      newInput.setAttribute('aria-invalid', 'true');
      newInput.focus();
      return;
    }
    newInput.value = ''; // at once, so a second Enter does not add it twice
    try {
      await createFeature(name);
      shell.toast('Feature tracked');
    } catch (err) {
      if (!newInput.value) newInput.value = name; // the typed name is not lost with the write
      shell.toast(`Could not save: ${err instanceof Error ? err.message : String(err)}`, { kind: 'error' });
    }
  };
  newInput.addEventListener('keydown', (ev) => {
    if ((ev as KeyboardEvent).key === 'Enter') {
      ev.preventDefault();
      void add();
    }
  });

  const list = h('div', { class: 'drafts' });
  const render = async () => {
    const data = await getWatchlistData();
    const entries = buildWatchlist(data.features, data.requests, data.leads, data.visits);
    replaceChildren(
      list,
      entries.length
        ? entries.map(entryCard)
        : empty('Nothing tracked yet.', 'Features land here from the quick-log picker, or add one above.'),
    );
  };

  replaceChildren(
    host,
    section(
      { title: 'Track a feature' },
      h(
        'div',
        { class: 'featurepicker__new' },
        newInput,
        btn('Add', { variant: 'subtle', onClick: () => void add() }),
      ),
    ),
    validation,
    list,
  );
  void render();
  return onStoreChange(() => void render());
};
